import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
 ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import Highcharts from 'projects/gtw-elements/src/app/core/highcharts-init';

import { AppService } from 'projects/gtw-elements/src/app/app.service';
import { DEFAULT_CHART_CONFIG } from '../../global-defaults/default-chart-options';
import * as _ from 'lodash-es';
import { DataFormatterService } from '../../services/data-formatter.service';
import { ExDashboardEventService } from '../../services/event.service';

@Component({
    selector: 'gtw-ex-chart',
    templateUrl: './ex-chart.component.html',
    styleUrls: ['./ex-chart.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ExChartComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  options: any;

  @ViewChild('chartElem', { static: true })
  chartElem!: ElementRef<HTMLDivElement>;

  chart!: any;
  chartConfig!: any;
  container: Element | null = null;
  /** Deferred reflow timers from `render()` — cleared on destroy. */
  private renderReflowHandles: ReturnType<typeof setTimeout>[] = [];
  /**
   * Deferred reflow RAF ids from `render()`. Tracked so a re-render or
   * destroy can cancel any in-flight `requestAnimationFrame` callbacks
   * before they hit a stale/destroyed Highcharts instance — fixes
   * `Cannot read properties of undefined (reading 'chart')` thrown from
   * `Highcharts.setAnimation` when `setSize` runs on a chart whose
   * internal state was wiped by destroy.
   */
  private renderReflowRafIds: number[] = [];
  /**
   * Monotonically incremented every time `render()` runs. Each deferred
   * reflow callback captures the cycle token at schedule time and bails
   * out if `this` has moved on to a newer cycle — prevents the previous
   * render's microtask/RAF/timeout from operating on the next render's
   * chart instance (which may not yet have its full animation state).
   */
  private renderCycleToken = 0;
  /**
   * Subscription to the dedicated `component-<id>:chartType` event channel.
   * Both the Highcharts hamburger menu and `ex-component-container`'s cog
   * popover broadcast on this channel so chart-type swaps update the live
   * chart in-place via `chart.update`, instead of going through the heavy
   * `:reload` event that destroys / recreates `gtw-ex-chart` (which was the
   * root cause of the "chart visible only after a click" bug).
   */
  private chartTypeChangeSub?: Subscription;
  private bodyResizeObserver: ResizeObserver | null = null;
  /** Coalesced rAF for ResizeObserver — must not drop updates or half-width cards stay 0×w until click. */
  private layoutRafId = 0;
  /** Bottom-row / off-axis charts: reflow when the card intersects the viewport. */
  private visibilityObserver: IntersectionObserver | null = null;
  private visibilityRafId = 0;
  private chartLifecycleRafId = 0;
  private readinessRetryHandle: ReturnType<typeof setTimeout> | null = null;
  /** Debounced window-resize fallback for shells that don't resize observed cards reliably. */
  private windowResizeRafId = 0;
  private windowResizeListener: (() => void) | null = null;
  private baseChartLoadEvent?: (this: any, ev?: any) => void;
  id: string = '';
  currentTheme: string = 'theme-black';
  defaultThemeConfig: any;

  private readonly singleSeriesChartTypes = [
    'separator',
    'pie',
    'donut',
    'packedbubble',
    'waterfall',
  ];

  private readonly multiSeriesChartTypes = [
    'separator',
    'line',
    'spline',
    'area',
    'bar',
    'column',
  ];
  currentDatalabelFormatter: string | (() => void) = '';
  currentStackLabelFormatter: string | (() => void) = '';
  currentTooltipFormatter: string | (() => void) = '';

  constructor(
    private appService: AppService,
    private eventService: ExDashboardEventService,
    private dataFormatterService: DataFormatterService
  ) { }

  ngOnInit(): void {
    this.setupDefaults();

    this.setupFormatters();

    this.setupChangeChartTypeOptions();

    this.setupSeries();

    if (
      this.chartConfig.series.length === 1 &&
      this.chartConfig.customColors &&
      this.chartConfig.customColors.enabled
    ) {
      this.setupCustomColors();
    }

    if (
      this.chartConfig.chart.type &&
      this.multiSeriesChartTypes.includes(
        this.chartConfig.chart.type.toLowerCase()
      )
    )
      this.setupSortingOptions();

    this.setupTheme();

    this.chartTypeChangeSub = this.eventService
      .on('component-' + this.options.component_id + ':chartType')
      .subscribe((payload: any) => {
        const newType =
          payload && typeof payload === 'object'
            ? payload.newType ?? payload.type
            : payload;
        this.applyChartTypeChange(newType);
      });
  }

  ngAfterViewInit(): void {
    // Defer past this CD frame + paint so flex parents (submission shell) have
    // committed widths/heights before Highcharts measures the card body.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.render());
    });
  }

  private setupTheme() {
    this.currentTheme =
      this.appService.USER_SETTINGS.user.selected_theme || 'theme-black';

    this.defaultThemeConfig = _.cloneDeep(this.chartConfig);
    if (this.currentTheme === 'theme-black') {
      // this.options = Highcharts.merge(this.options,darkThemeHighcharts);
    }
  }

  private setupDefaults() {
    this.id = 'component-' + this.options.component_id;

    this.chartConfig = _.merge(
      _.cloneDeep(DEFAULT_CHART_CONFIG),
      this.options.extraOptions.componentSettings
    );
    const baseEvents = (this.chartConfig.chart?.events || {}) as {
      load?: (this: any, ev?: any) => void;
      render?: (this: any, ev?: any) => void;
    };
    this.baseChartLoadEvent =
      typeof baseEvents.load === 'function' ? baseEvents.load : undefined;

    if (
      this.options.extraOptions.componentSettings.isCombinationPie &&
      this.options.data?.series?.[0]?.data?.[0]
    ) {
      this.options.data.series[0].data[0].selected = true;
      this.options.data.series[0].data[0].sliced = true;
    }
    this.chartConfig.xAxis.categories = this.options.data.categories;
    if (!this.chartConfig.xAxis.categories) {
      this.chartConfig.xAxis.type = 'category';
    }
    // Clone so Highcharts.destroy() cannot strip or mutate resolver-owned series
    // before the next `renderComponent` assigns fresh strategy output.
    this.chartConfig.series = _.cloneDeep(this.options.data?.series ?? []);
    // Keep breadcrumbs configured on the dashboard (the "Back to COUNT"
    // button). Assigning `data.drilldown` outright drops that config, and
    // Highcharts 11 then shows only the "parent / child" path.
    if (this.options.data?.drilldown) {
      const drilldownChrome = this.chartConfig.drilldown;
      this.chartConfig.drilldown = _.merge(
        {},
        _.cloneDeep(this.options.data.drilldown),
        drilldownChrome || {}
      );
    }
    this.chartConfig.plotOptions.series.point = {};
    this.chartConfig.plotOptions.series.point.events = {};
    const that = this;
    this.chartConfig.plotOptions.series.point.events.click = function (event: any) {
      // Highcharts calls this with the point as `this`. Fall back to
      // event.point so a chart-level `this` cannot drop name and series.
      const point = isHighchartsPoint(this) ? this : event?.point || this;
      that.eventService.broadcast(that.options.component_id, point);
      if (that.options.extraOptions.componentSettings.eventMessage)
        that.eventService.broadcast(
          that.options.extraOptions.componentSettings.eventMessage,
          point
        );
    };

    this.chartConfig.exporting.chartOptions = {
      title: {
        text: this.options.title,
      },
    };
  }

  private setupFormatters() {
    const that = this;
    const dataLabels = this.chartConfig.plotOptions.series.dataLabels || {
      enabled: false,
    };
    this.currentDatalabelFormatter = _.cloneDeep(dataLabels.formatter);
    if (dataLabels.enabled && typeof dataLabels.formatter !== 'function') {
      dataLabels.formatter = dataLabelFormatter;
    }
    const seriesLess = ['pie', 'variablepie'];
    if (
      seriesLess.includes(this.chartConfig.chart.type) &&
      this.chartConfig.plotOptions.series.dataLabels.enabled
    ) {
      this.chartConfig.plotOptions[this.chartConfig.chart.type].dataLabels =
        this.chartConfig.plotOptions.series.dataLabels;
    }
    const stackLabels = this.chartConfig.yAxis.stackLabels || {
      enabled: false,
    };
    this.currentStackLabelFormatter = _.cloneDeep(stackLabels.formatter);
    if (
      stackLabels.enabled &&
      stackLabels.formatter &&
      !stackLabels.format &&
      typeof stackLabels.formatter !== 'function'
    ) {
      stackLabels.formatter = stackLabelsFormatter;
    }

    console.log('stacklabels rendered', stackLabels);
    const tooltip = this.chartConfig.tooltip || { enabled: false };
    this.currentTooltipFormatter = _.cloneDeep(tooltip.formatter);

    if (tooltip.enabled && typeof tooltip.formatter !== 'function') {
      tooltip.pointFormatter = tooltipFormatter;
      tooltip.useHTML = true;
      delete tooltip.formatter;
    }

    function dataLabelFormatter(this: any, e: any, ops: any) {
      const formatter = that.dataFormatterService.getFormatter(
        that.currentDatalabelFormatter as string
      );
      if (!formatter) {
        return this.y;
      }

      let format = formatter.func
        ? formatter.func(this.y, this.point.isNegative, this.point.name)
        : this.y;
      if (that.chartConfig.plotOptions.series.dataLabels.seriesAsLabel) {
        format = this.point.series.name;
      }

      // const positiveValue = "<span class='text-center' style='margin: 0px !important;font-size : 12px;'>" + format + ' </span>';
      // const negativeValue = "<span class='text-center' style='margin: 0px !important;font-size : 12px;color:red'>" + format + ' </span>';

      if (this.y === 0) {
        return null;
      } else {
        // return (this.y < 0 ? negativeValue : positiveValue);
        return format;
      }
    }

    function stackLabelsFormatter(this: any, e: any, ops: any) {
      const formatter = that.dataFormatterService.getFormatter(
        that.currentStackLabelFormatter as string
      );
      if (!formatter) {
        return this.total;
      }
      let percent = 0;
      if (formatter.name === 'Percent') {
        percent = (this.points['0'][0] / this.points['0'][1]) * 100;
      }
      const positiveValue =
        "<span class='text-center' style='margin: 0px !important;font-size : 12px;'>" +
        (formatter.func
          ? formatter.name === 'Percent'
            ? formatter.func(Math.abs(percent))
            : formatter.func(Math.abs(this.total))
          : Math.abs(this.total)) +
        ' </span>';
      const negativeValue =
        "<span class='text-center' style='margin: 0px !important;font-size : 12px;color:red'>" +
        (formatter.func
          ? formatter.name === 'Percent'
            ? formatter.func(Math.abs(percent))
            : formatter.func(Math.abs(this.total))
          : Math.abs(this.total)) +
        ' </span>';

      return this.total < 0 || this.percent < 0 ? negativeValue : positiveValue;
    }
    function tooltipFormatter(this: any, e: any) {
      const formatter = that.dataFormatterService.getFormatter(
        that.currentTooltipFormatter as string
      );
      if (!formatter || formatter.name === 'None') {
        return this.tooltipFormatter(e);
      }
      const legend = '<span style="color:' + this.color + '">●</span>';
      const seriesName =
        '<span class="text-center" style="margin: 0px !important;font-size : 12px;">' +
        this.series.name +
        ':</span>';
      const positiveValue =
        "<span class='text-center' style='margin: 0px !important;font-size : 12px;font-weight:bold'>" +
        (formatter.func
          ? formatter.func(Math.abs(this.y), this.isNegative, this.name)
          : Math.abs(this.y)) +
        ' </span></br>';
      const negativeValue =
        "<span class='text-center' style='margin: 0px !important;font-size : 12px;color:red';font-weight:bold>" +
        (formatter.func
          ? formatter.func(Math.abs(this.y), this.isNegative, this.name)
          : Math.abs(this.y)) +
        ' </span></br>';

      // if (this.y === 0) {
      //     return null;
      // }
      // else {
      return (
        '<div>' +
        legend +
        (this.series.name && this.category ? seriesName : '') +
        (this.y < 0 ? negativeValue : positiveValue) +
        '</div>'
      );
      // }
    }
  }

  private setupChangeChartTypeOptions() {
    if (!this.chartConfig.chart.type) return;

    const chartTypes = this.singleSeriesChartTypes.includes(
      this.chartConfig.chart.type.toLowerCase()
    )
      ? this.singleSeriesChartTypes
      : this.multiSeriesChartTypes;
    for (let i = 0; i < chartTypes.length; i++) {
      const key = chartTypes[i];
      if (key === 'separator') {
        this.chartConfig.exporting.menuItemDefinitions[key] = { separator: true };
      } else {
        const menuKey = key;
        this.chartConfig.exporting.menuItemDefinitions[key] = {
          text: key === 'variablepie' ? 'Donut' : key,
          // Highcharts calls onclick with an unstable `event.target` (often not the
          // menu row). Bind the menu key in a closure so line→spline (etc.) always
          // applies the correct type and the resolver reload receives valid data.
          onclick: () => this.handleChartTypeMenuSelect(menuKey),
        };
      }
      this.chartConfig.exporting.buttons.contextButton.menuItems.unshift(key);
    }
  }

  /**
   * Highcharts hamburger-menu chart-type switch. `menuKey` is the exporting
   * definition key (e.g. `line`, `spline`, `column`), not DOM text — avoids
   * empty/wrong types when `event.target` is not the menu item node.
   *
   * Broadcasts `component-<id>:chartType` so the live chart updates in-place
   * via `chart.update` (see `applyChartTypeChange`). The shared event channel
   * lets the cog popover in `ex-component-container` reuse the same path.
   */
  private handleChartTypeMenuSelect(menuKey: string): void {
    if (menuKey === 'separator') {
      return;
    }
    if (!(this.chartConfig.series?.length > 0)) {
      return;
    }
    const raw = String(menuKey || '').toLowerCase().trim();
    if (!raw) {
      return;
    }
    this.eventService.broadcast(
      'component-' + this.options.component_id + ':chartType',
      { newType: raw }
    );
  }

  /**
   * Apply a chart-type change to the existing Highcharts instance via
   * `chart.update`, avoiding the destroy-and-recreate path that produced
   * the "chart only visible after a click" bug (the new chart used to mount
   * before its container's flex dimensions were finalised).
   *
   * `donut` is the legacy AngularJS display label for `variablepie` (see
   * `ui/app/components/admin/adminDashboards/component-settings-templates/
   * chart-component-tpl.html`). Map it to the Highcharts type name.
   */
  private applyChartTypeChange(newType: string): void {
    if (!this.chart || !newType) {
      return;
    }
    const t = String(newType).toLowerCase().trim();
    if (!t) {
      return;
    }
    const resolved = t === 'donut' ? 'variablepie' : t;

    this.options.extraOptions = this.options.extraOptions || {};
    this.options.extraOptions.componentSettings =
      this.options.extraOptions.componentSettings || {};
    this.options.extraOptions.componentSettings.chart =
      this.options.extraOptions.componentSettings.chart || {};
    this.options.extraOptions.componentSettings.chart.type = resolved;
    if (this.chartConfig?.chart) {
      this.chartConfig.chart.type = resolved;
    }

    // Drop per-series `type` so Highcharts inherits the new chart-level type
    // (otherwise a series staying `line` overrides a chart-level `spline`).
    if (Array.isArray(this.options.data?.series)) {
      this.options.data.series.forEach((s: any) => {
        if (s && typeof s === 'object') {
          delete s.type;
        }
      });
    }

    try {
      this.chart.update({ chart: { type: resolved } }, false);
      if (Array.isArray(this.chart.series)) {
        this.chart.series.forEach((s: any) => {
          try {
            s?.update?.({ type: resolved }, false);
          } catch {
            /* swallow per-series update failures (incompatible point shape) */
          }
        });
      }
      this.chart.redraw(false);
      this.ensureContextButtonVisible();
    } catch {
      /* keep chart resilient if Highcharts internals reject the update */
    }
  }

  /**
   * Highcharts context-button (hamburger) can remain visually suppressed right
   * after chart-type reload until the next interaction. Re-assert visibility
   * so menu access is immediate without requiring a click on the chart area.
   */
  private ensureContextButtonVisible(): void {
    const chart = this.chart as any;
    if (!chart) {
      return;
    }
    try {
      chart.exportingGroup?.attr?.({ opacity: 1, visibility: 'inherit' });
      if (Array.isArray(chart.exportSVGElements)) {
        chart.exportSVGElements.forEach((el: any) =>
          el?.attr?.({ opacity: 1, visibility: 'inherit' })
        );
      }
      chart.redraw(false);
    } catch {
      // no-op: keep chart rendering resilient across Highcharts internals
    }
  }

  private setupSeries() {
    if (
      this.options.extraOptions.componentSettings.dataOverrides &&
      this.options.extraOptions.componentSettings.dataOverrides.series
    ) {
      _.each(this.options.data.series, (series) => {
        const editedSeries =
          _.find(
            this.options.extraOptions.componentSettings.dataOverrides.series,
            ['name', series.name]
          ) ||
          _.get(
            this.options.extraOptions.componentSettings.dataOverrides.series,
            0
          );
        if (editedSeries) {
          if (editedSeries.color || series.color)
            series.color = editedSeries.color || series.color;
          if (editedSeries.stacking) series.stacking = editedSeries.stacking;
          series.name = editedSeries.displayName || series.name;
          series.allowDrillToNode = _.isUndefined(editedSeries.allowDrillToNode)
            ? series.allowDrillToNode
            : editedSeries.allowDrillToNode;
          series.colorByPoint = _.isUndefined(editedSeries.colorByPoint)
            ? series.colorByPoint
            : editedSeries.colorByPoint;
        }
      });
    }
  }

  private setupCustomColors() {
    this.chartConfig.series[0].data = _.reverse(_.sortBy(this.chartConfig.series[0].data, 'y'));
    if (
      this.chartConfig.series[0].data[0] &&
      this.chartConfig.series[0].data[0].y !== 0
    )
      this.chartConfig.series[0].data[0].color =
        Highcharts.getOptions().colors![1];
    if (
      this.chartConfig.series[0].data[1] &&
      this.chartConfig.series[0].data[1].y !== 0
    )
      this.chartConfig.series[0].data[1].color =
        Highcharts.getOptions().colors![2];
    if (
      this.chartConfig.series[0].data[2] &&
      this.chartConfig.series[0].data[2].y !== 0
    )
      this.chartConfig.series[0].data[2].color =
        Highcharts.getOptions().colors![3];
  }

  private setupSortingOptions() {
    [
      { name: 'separator', text: 'separator' },
      { name: 'sort-desc', text: 'Sort Descending' },
      { name: 'sort-asc', text: 'Sort Ascending' },
    ].forEach((item) => {
      if (item.name === 'separator') {
        this.chartConfig.exporting.menuItemDefinitions[item.name] = {
          separator: true,
        };
      } else {
        this.chartConfig.exporting.menuItemDefinitions[item.name] = {
          text: item.text,
          // See handleChartTypeMenuSelect: exporting calls onclick.apply(chart).
          onclick: () => this.sortChartData(item.name),
        };
      }
      this.chartConfig.exporting.buttons.contextButton.menuItems.unshift(
        item.name
      );
    });
  }

  private sortChartData(sort: string) {
    const order = sort.split('-')[1];
    const arrayToSort: any[] = [];

    /**
     * Interchanging x & y axis of 2d array and calculate sum of each row
     * (rotate 2D arary by 90deg clockwise)
     */
    const isObject = typeof this.chart.options.series![0].data[0] == 'object';
    this.chart.options.series[0].data.forEach((x: any, i: number) => {
      let sum = 0;
      this.chart.options.series.forEach((y: any) => {
        sum += isObject ? y.data[i].y : y.data[i];

        if (!arrayToSort[i]) {
          arrayToSort[i] = [];
        }
        arrayToSort[i].push(y.data[i]);
      });
      arrayToSort[i].unshift(this.chart.options.xAxis[0].categories[i]);
      arrayToSort[i].unshift(sum);
    });

    // Sorting array based on first element (sum)
    arrayToSort.sort(function(a, b) {
      return order == 'asc' ? a[0] - b[0] : b[0] - a[0];
    });

    // rotate sortedArray by 90deg anti-clockwise
    for (let i = 2; i < arrayToSort[0].length; i++) {
      const temp: any[] = [];
      const categories: any[] = [];
      arrayToSort.forEach(function(y, j) {
        // temp.push([arrayToSort[j][1], y[i]])
        temp.push(isObject ? y[i].y : y[i]);
        categories.push(arrayToSort[j][1]);
      });

      this.chart.series[i - 2].setData(temp);
      this.chart.xAxis[0].setCategories(categories);
    }
  }

  /**
   * `component.height` from dashboard JSON (e.g. `450px`) minus the fixed
   * `.gt-dashboard-ex-header` band (~35px). When flex layout has not yet
   * assigned `clientHeight` to the body, this still gives Highcharts a
   * valid plot height so the first paint is not an empty box.
   */
  private chartBodyHeightFromConfig(): number {
    const raw = this.options?.height;
    if (raw == null) {
      return 0;
    }
    const s = String(raw).trim();
    if (!s) {
      return 0;
    }
    if (s.endsWith('%') && !/px/i.test(s)) {
      return 0;
    }
    const px = /^(\d+(?:\.\d+)?)\s*px\s*$/i.exec(s);
    if (px) {
      const card = parseFloat(px[1]);
      if (!isNaN(card) && card > 50) {
        return Math.max(80, Math.round(card - 35));
      }
    }
    const n = parseFloat(s);
    if (!isNaN(n) && n > 50) {
      return Math.max(80, Math.round(n - 35));
    }
    return 0;
  }

  private clearRenderReflows(): void {
    for (const t of this.renderReflowHandles) {
      clearTimeout(t);
    }
    this.renderReflowHandles = [];
    if (this.readinessRetryHandle) {
      clearTimeout(this.readinessRetryHandle);
      this.readinessRetryHandle = null;
    }
    for (const id of this.renderReflowRafIds) {
      cancelAnimationFrame(id);
    }
    this.renderReflowRafIds = [];
  }

  private disconnectBodyResizeObserver(): void {
    if (this.layoutRafId) {
      cancelAnimationFrame(this.layoutRafId);
      this.layoutRafId = 0;
    }
    if (this.visibilityRafId) {
      cancelAnimationFrame(this.visibilityRafId);
      this.visibilityRafId = 0;
    }
    if (this.chartLifecycleRafId) {
      cancelAnimationFrame(this.chartLifecycleRafId);
      this.chartLifecycleRafId = 0;
    }
    if (this.windowResizeRafId) {
      cancelAnimationFrame(this.windowResizeRafId);
      this.windowResizeRafId = 0;
    }
    if (this.windowResizeListener) {
      window.removeEventListener('resize', this.windowResizeListener);
      this.windowResizeListener = null;
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
    if (this.bodyResizeObserver) {
      this.bodyResizeObserver.disconnect();
      this.bodyResizeObserver = null;
    }
  }

  private render() {
    this.clearRenderReflows();
    this.disconnectBodyResizeObserver();
    const cycleToken = ++this.renderCycleToken;

    this.container = this.chartElem.nativeElement.closest(
      '.gt-dashboard-ex-body'
    );
    const body = this.container as HTMLElement | null;
    const card = this.chartElem.nativeElement.closest(
      '.gt-dashboard-ex'
    ) as HTMLElement | null;

    const measureBodyHeight = (): number => {
      const cardCap = card && card.clientHeight > 50
        ? Math.max(80, card.clientHeight - 35)
        : 0;
      const clamp = (value: number): number => {
        if (cardCap > 40 && value > cardCap) {
          return cardCap;
        }
        return value;
      };
      const direct = body?.clientHeight ?? 0;
      if (direct > 40) {
        return clamp(direct);
      }
      if (cardCap > 40) {
        return cardCap;
      }
      const parent = this.chartElem.nativeElement.parentElement;
      const ph = parent?.clientHeight ?? 0;
      if (ph > 40) {
        return clamp(ph);
      }
      const fromCfg = this.chartBodyHeightFromConfig();
      if (fromCfg > 40) {
        return clamp(fromCfg);
      }
      return clamp(280);
    };

    let cycleChart: any = null;
    let readinessRetryAttempts = 0;
    const maxReadinessRetryAttempts = 12;

    const reflowToContainer = () => {
      // Stale-cycle guard: a newer `render()` has already kicked off, or
      // the component is being torn down. Either way this callback no
      // longer owns `this.chart`, so skip it.
      if (this.renderCycleToken !== cycleToken) {
        return;
      }
      const chart = this.chart;
      // Highcharts nulls `renderer` (and clears `options`) on `destroy()`.
      // If either is missing, the chart is mid-teardown / mid-rebuild and
      // calling `setSize` would crash inside `setAnimation` with
      // `Cannot read properties of undefined (reading 'chart')`.
      if (!chart || chart !== cycleChart || !chart.renderer || !chart.options) {
        return;
      }
      try {
        const h = measureBodyHeight();
        const renderEl = chart.renderTo as HTMLElement;
        const w =
          renderEl?.parentElement?.clientWidth ??
          renderEl?.clientWidth ??
          0;
        if (w > 20 && h > 40) {
          chart.setSize(w, h, false);
        } else
        if (h > 40) {
          chart.setSize(null, h, false);
        }
        chart.reflow();
        this.ensureContextButtonVisible();
      } catch (e) {
        // Defensive: Highcharts internals can still throw when a chart is
        // resized while a parent (e.g. tab content) is being detached. Log
        // once and move on — the next ResizeObserver/IntersectionObserver
        // tick will reflow correctly when the container is laid out again.
        console.warn('[gtw-ex-chart] reflow skipped', e);
      }
    };

    const isChartLayoutReady = (): boolean => {
      const chart = this.chart;
      if (!chart || chart !== cycleChart || !chart.renderer || !chart.options) {
        return false;
      }
      const renderEl = chart.renderTo as HTMLElement | null;
      const containerWidth =
        renderEl?.parentElement?.clientWidth ??
        renderEl?.clientWidth ??
        0;
      const plotWidth = Number(chart.plotWidth ?? 0);
      const plotHeight = Number(chart.plotHeight ?? 0);
      return containerWidth > 20 && plotWidth > 20 && plotHeight > 20;
    };

    const scheduleReadinessRetry = () => {
      if (this.renderCycleToken !== cycleToken) {
        return;
      }
      if (isChartLayoutReady()) {
        return;
      }
      if (readinessRetryAttempts >= maxReadinessRetryAttempts) {
        return;
      }
      if (this.readinessRetryHandle) {
        return;
      }
      const delayMs = Math.min(180 + readinessRetryAttempts * 120, 700);
      this.readinessRetryHandle = setTimeout(() => {
        this.readinessRetryHandle = null;
        readinessRetryAttempts += 1;
        reflowToContainer();
        scheduleReadinessRetry();
      }, delayMs);
    };

    const scheduleLifecycleReflow = () => {
      if (this.renderCycleToken !== cycleToken) {
        return;
      }
      if (this.chartLifecycleRafId) {
        cancelAnimationFrame(this.chartLifecycleRafId);
      }
      this.chartLifecycleRafId = requestAnimationFrame(() => {
        this.chartLifecycleRafId = 0;
        reflowToContainer();
      });
    };

    const component = this;
    const chartEvents = {
      ...(this.chartConfig.chart?.events || {}),
      load: function(this: any, ev?: any) {
        // Preserve existing consumer-defined load callback first.
        if (typeof component.baseChartLoadEvent === 'function') {
          component.baseChartLoadEvent.call(this, ev);
        }
        scheduleLifecycleReflow();
      },
    };

    const h0 = measureBodyHeight();
    this.chartConfig.chart = {
      ...(this.chartConfig.chart || {}),
      height: h0,
      events: chartEvents,
    };
    this.chart = Highcharts.chart(this.chartElem.nativeElement, this.chartConfig);
    cycleChart = this.chart;
    scheduleLifecycleReflow();
    scheduleReadinessRetry();

    queueMicrotask(() => reflowToContainer());

    const outerRaf = requestAnimationFrame(() => {
      // Outer RAF popped — drop its id; track the inner RAF instead so a
      // teardown between the two frames cancels it cleanly.
      this.renderReflowRafIds = this.renderReflowRafIds.filter(
        id => id !== outerRaf
      );
      const innerRaf = requestAnimationFrame(() => {
        this.renderReflowRafIds = this.renderReflowRafIds.filter(
          id => id !== innerRaf
        );
        reflowToContainer();
      });
      this.renderReflowRafIds.push(innerRaf);
    });
    this.renderReflowRafIds.push(outerRaf);

    for (const ms of [0, 120, 350, 600, 900]) {
      this.renderReflowHandles.push(
        setTimeout(() => reflowToContainer(), ms)
      );
    }

    const layoutTarget = card || body;
    if (typeof ResizeObserver !== 'undefined' && layoutTarget) {
      this.bodyResizeObserver = new ResizeObserver(() => {
        if (this.layoutRafId) {
          cancelAnimationFrame(this.layoutRafId);
        }
        this.layoutRafId = requestAnimationFrame(() => {
          this.layoutRafId = 0;
          reflowToContainer();
        });
      });
      this.bodyResizeObserver.observe(layoutTarget);
    }

    const visTarget = card || this.chartElem.nativeElement;
    if (typeof IntersectionObserver !== 'undefined' && visTarget) {
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              if (this.visibilityRafId) {
                cancelAnimationFrame(this.visibilityRafId);
              }
              this.visibilityRafId = requestAnimationFrame(() => {
                this.visibilityRafId = 0;
                reflowToContainer();
                // Tab / pane visibility changes can happen after bounded retries
                // already stopped; re-arm bounded readiness when becoming visible.
                readinessRetryAttempts = 0;
                scheduleReadinessRetry();
              });
              break;
            }
          }
        },
        { root: null, rootMargin: '0px', threshold: [0, 0.01, 0.1] },
      );
      this.visibilityObserver.observe(visTarget);
    }

    this.windowResizeListener = () => {
      if (this.windowResizeRafId) {
        cancelAnimationFrame(this.windowResizeRafId);
      }
      this.windowResizeRafId = requestAnimationFrame(() => {
        this.windowResizeRafId = 0;
        reflowToContainer();
        readinessRetryAttempts = 0;
        scheduleReadinessRetry();
      });
    };
    window.addEventListener('resize', this.windowResizeListener, { passive: true });
  }

  ngOnDestroy(): void {
    this.chartTypeChangeSub?.unsubscribe();
    this.clearRenderReflows();
    this.disconnectBodyResizeObserver();
    this.chart?.destroy();
  }
}

function isHighchartsPoint(value: any): boolean {
  return !!value && !Array.isArray(value.series) && !!value.series;
}
